/**
 * useCommunicationDetail.js
 * Logic hook for CommunicationDetailDialog — state, effects, and API handlers.
 * Stage 2 logic refactor of ActivityDialogs.
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { activityAPI } from '../../../../services/api';
import { associationAPI } from '../../../../services/api';
import api from '../../../../services/api';
import { useAuth } from '../../../../context/AuthContext';
import { useConfirm } from '../../../../components/common/ConfirmDialog';

export function useCommunicationDetail(communication, {
  associations = [],
  users = [],
  contactEntries = [],
  tasks = [],
  horses = [],
  accounts = [],
  showAccountDropdown = false,
  onCommunicationUpdate,
  canEditAccount,
  onOpenChange,
  onSuccess,
}) {
  const { user: currentUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    communication_method: 'telephone',
    start_date_time: '',
    association_id: '',
    horse_id: '',
    linked_account_id: '',
    assigned_to_user_id: '',
    contact_entry_id: '',
    is_sensitive: false,
    linked_task_ids: [],
  });
  const [filteredAssociations, setFilteredAssociations] = useState([]);
  const [dynamicContactEntries, setDynamicContactEntries] = useState([]);

  // Sync communication → formData when communication changes
  useEffect(() => {
    if (communication) {
      setFormData({
        activity_name: communication.activity_name || '',
        description: communication.description || '',
        communication_method: communication.communication_method || 'telephone',
        start_date_time: communication.start_date_time
          ? new Date(communication.start_date_time).toISOString().split('T')[0]
          : '',
        association_id: communication.association_id || '',
        horse_id: communication.horse_id || '',
        linked_account_id: communication.linked_account_id || communication.crm_entity_id || '',
        assigned_to_user_id: communication.assigned_to_user_id || '',
        contact_entry_id: communication.contact_entry_id || '',
        is_sensitive: communication.is_sensitive || false,
        linked_task_ids: communication.linked_task_ids || [],
      });
    }
  }, [communication]);

  // Fetch enriched associations when account changes
  useEffect(() => {
    const fetchAssociationsForAccount = async () => {
      if (showAccountDropdown && formData.linked_account_id) {
        try {
          const response = await associationAPI.list(formData.linked_account_id);
          const list = response.data || [];
          const enriched = await Promise.all(
            list.map(async (assoc) => {
              const otherEntityId = assoc.crm_entity_1_id === formData.linked_account_id
                ? assoc.crm_entity_2_id
                : assoc.crm_entity_1_id;
              try {
                const entityRes = await api.get(`/crm-entities/${otherEntityId}`);
                return { ...assoc, entityName: entityRes.data.system_name };
              } catch {
                return { ...assoc, entityName: 'Unknown Contact' };
              }
            })
          );
          setFilteredAssociations(enriched);
        } catch {
          setFilteredAssociations([]);
        }
      } else if (!showAccountDropdown) {
        setFilteredAssociations(associations);
      } else {
        setFilteredAssociations([]);
      }
    };
    fetchAssociationsForAccount();
  }, [formData.linked_account_id, showAccountDropdown, associations]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch contact entries dynamically when account changes (Horse context edit mode)
  useEffect(() => {
    if (showAccountDropdown && formData.linked_account_id) {
      api.get(`/contact-entries?crm_entity_id=${formData.linked_account_id}`)
        .then(r => setDynamicContactEntries(r.data || []))
        .catch(() => setDynamicContactEntries([]));
    } else {
      setDynamicContactEntries([]);
    }
  }, [formData.linked_account_id, showAccountDropdown]);

  // Effective contact entries: dynamic in horse context, prop-passed in account context
  const effectiveContactEntries = showAccountDropdown ? dynamicContactEntries : contactEntries;

  // Filter contact entries by communication method
  const filteredContactEntries = (() => {
    const method = formData.communication_method;
    if (method === 'telephone' || method === 'text')                return effectiveContactEntries.filter(e => e.contact_type === 'phone');
    if (method === 'email_direct' || method === 'email_newsletter') return effectiveContactEntries.filter(e => e.contact_type === 'email');
    if (method === 'mailer')                                        return effectiveContactEntries.filter(e => e.contact_type === 'address');
    if (method === 'in_person' || method === 'video_conference')    return [];
    return effectiveContactEntries; // 'other' and unknown → show all
  })();

  const handleSave = async () => {
    if (!communication?.id) return;
    setSaving(true);
    try {
      await activityAPI.update(communication.id, {
        ...formData,
        start_date_time: formData.start_date_time
          ? new Date(formData.start_date_time).toISOString()
          : new Date().toISOString(),
      });
      toast.success('Communication updated');
      if (onCommunicationUpdate) onCommunicationUpdate({ ...communication, ...formData });
      setIsEditing(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to update communication');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!communication?.id) return;
    if (!await confirm({ title: 'Are you sure you want to delete this communication?', destructive: true })) return;
    setSaving(true);
    try {
      await activityAPI.delete(communication.id);
      toast.success('Communication deleted');
      onOpenChange(false);
      onSuccess?.();
    } catch {
      toast.error('Failed to delete communication');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (communication) {
      setFormData({
        activity_name: communication.activity_name || '',
        description: communication.description || '',
        communication_method: communication.communication_method || 'telephone',
        start_date_time: communication.start_date_time
          ? new Date(communication.start_date_time).toISOString().split('T')[0]
          : '',
        association_id: communication.association_id || '',
        horse_id: communication.horse_id || '',
        assigned_to_user_id: communication.assigned_to_user_id || '',
        contact_entry_id: communication.contact_entry_id || '',
        is_sensitive: communication.is_sensitive || false,
        linked_task_ids: communication.linked_task_ids || [],
      });
    }
    setIsEditing(false);
  };

  const getContactDisplay = (entry) => {
    if (!entry) return null;
    if (entry.contact_type === 'email') return entry.email_address;
    if (entry.contact_type === 'phone') return entry.phone_number;
    if (entry.contact_type === 'address') return `${entry.street}, ${entry.city}`;
    return 'Contact';
  };

  // Derived values
  const canEdit = canEditAccount !== undefined
    ? canEditAccount
    : (communication && (communication.created_by_id === currentUser?.id
        || currentUser?.roles?.includes('tenant_system_admin')
        || currentUser?.roles?.includes('system_owner')));
  const linkedAssoc   = associations.find(a => a.id === communication?.association_id);
  const assignedUser  = users.find(u => u.id === communication?.assigned_to_user_id);
  const contactEntry  = contactEntries.find(c => c.id === communication?.contact_entry_id);
  const linkedTasks = tasks.filter(t =>
    t.linked_communication_ids?.includes(communication?.id) ||
    communication?.linked_task_ids?.includes(t.id)
  );

  // Linked horse — resolved from horses prop
  const linkedHorse = horses.find(h => h.id === communication?.horse_id) || null;

  // Linked account — resolved from accounts prop; only shown in horse context
  // (in account context the account is already implicit — showing it is redundant)
  const linkedAccount = showAccountDropdown
    ? accounts.find(a =>
        a.id === (communication?.linked_account_id || communication?.crm_entity_id)
      ) || null
    : null;

  return {
    isEditing, setIsEditing,
    saving,
    showTaskDialog, setShowTaskDialog,
    formData, setFormData,
    filteredAssociations,
    effectiveContactEntries: filteredContactEntries,
    handleSave, handleDelete, cancelEdit,
    getContactDisplay,
    canEdit, linkedAssoc, assignedUser, contactEntry, linkedTasks,
    linkedHorse, linkedAccount,
  };
}
